import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ICompanyGroup } from "store/companyGroups";
import { createCompanyGroup, updateCompanyGroup } from "store/companyGroups/company-groups-use-cases";
import { useAppDispatch } from "store/store";

interface Props {
    open: boolean;
    onClose: () => void;
    group?: ICompanyGroup | null;
}

const CompanyGroupFormModal = ({ open, onClose, group }: Props) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<{ name: string }>({ defaultValues: { name: "" } });

    useEffect(() => {
        if (open) reset({ name: group?.name ?? "" });
    }, [open, group, reset]);

    const onSubmit = async ({ name }: { name: string }) => {
        const action = group
            ? updateCompanyGroup({ id: group.id, name: name.trim() })
            : createCompanyGroup({ name: name.trim() });
        try {
            await (dispatch(action) as any).unwrap();
            onClose();
        } catch {
            // toast handled in the slice
        }
    };

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={group ? t("CompanyGroups.EditGroup") : t("CompanyGroups.CreateGroup")}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2 min-w-90">
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: t("CompanyGroups.GroupNameRequired") as string }}
                    render={({ field }) => (
                        <StyledTextField
                            {...field}
                            autoFocus
                            fullWidth
                            label={t("CompanyGroups.GroupName")}
                            placeholder={t("CompanyGroups.GroupNamePlaceholder")}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            autoComplete="group-name"
                        />
                    )}
                />
                <div className="flex justify-end gap-2">
                    <StyledButton type="button" buttonType="secondary" onClick={onClose}>
                        {t("CompanyGroups.Cancel")}
                    </StyledButton>
                    <StyledButton type="submit" buttonType="primary" loading={isSubmitting}>
                        {group ? t("CompanyGroups.Save") : t("CompanyGroups.CreateGroup")}
                    </StyledButton>
                </div>
            </form>
        </ModalBox>
    );
};

export default CompanyGroupFormModal;
